import React from 'react'
import { PoolListAccordionListItemDetail } from '../PoolList/styled'
import PoolLogo from 'pages/ExplorePools/PoolLogo'
import Tag from '../../../../components/Tag'
import { formattedFeeAmount } from 'utils'
import { roundToDecimalPlace } from 'utils/data'
import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { WETH9 } from '@uniswap/sdk-core'
import TagButton from 'components/TagButton'
import { useHistory } from 'react-router-dom'
import { formatUnits } from 'ethers/lib/utils'
import TokenLogo from 'pages/ExplorePools/TokenLogo'

interface PoolListCardProps {
  pool: any
  token0: any
  token1: any
  pairName: string
  isBestApr?: boolean
  isBestVolume?: boolean
  isBestTvl?: boolean
}

const PoolListCard: React.FC<PoolListCardProps> = ({
  pool,
  token0,
  token1,
  pairName,
  isBestApr,
  isBestVolume,
  isBestTvl,
}) => {
  const history = useHistory()
  const [copyStatus, setCopyStatus] = React.useState<string | null>(null)
  const saveLink = (address: string) => {
    if (address) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(address)
          .then(() => {
            setCopyStatus('Copied!')
            setTimeout(() => setCopyStatus(null), 5000)
          })
          .catch(() => {
            // Fallback to manual copy if writeText fails
            fallbackCopyTextToClipboard(address)
          })
      } else {
        // Fallback for older browsers
        fallbackCopyTextToClipboard(address)
      }
    }
  }

  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      if (successful) {
        setCopyStatus('Copied!')
      } else {
        setCopyStatus('Copy failed')
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err)
    }

    document.body.removeChild(textArea)
    setTimeout(() => setCopyStatus(null), 5000)
  }

  const addLPRoute = `/add${pool.version === 'v3' ? '/' : '/v2/'}${
    token0.token0Address === WETH9[CURRENT_CHAIN].address ? 'HYPE' : token0.token0Address
  }/${token1.token1Address === WETH9[CURRENT_CHAIN].address ? 'HYPE' : token1.token1Address}${
    pool.version === 'v3' ? `/${pool.fee}` : ''
  }`

  const isVolumeIncreasing = pool.totalPrevH24 && pool.h24 ? parseFloat(pool.h24) - parseFloat(pool.varH24) > 0 : null

  const volumeIncreasingIcon =
    isVolumeIncreasing !== null ? (
      isVolumeIncreasing ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#4AFB39"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-trending-up"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 17l6 -6l4 4l8 -8" />
          <path d="M14 7l7 0l0 7" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="red"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-trending-down"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M3 7l6 6l4 -4l8 8" />
          <path d="M21 10l0 7l-7 0" />
        </svg>
      )
    ) : (
      <></>
    )

  const poolTags = (
    <>
      {isBestApr && (
        <Tag variant={1} style={{ color: '#27AE60', background: 'rgba(39, 174, 96, 0.1)' }}>
          Best APR
        </Tag>
      )}
      {isBestVolume && (
        <Tag variant={1} style={{ color: '#2F80ED', background: 'rgba(47, 128, 237, 0.1)' }}>
          Best Volume
        </Tag>
      )}
      {isBestTvl && (
        <Tag variant={1} style={{ color: '#9B51E0', background: 'rgba(155, 81, 224, 0.1)' }}>
          Best TVL
        </Tag>
      )}
    </>
  )

  const isBestOfOne = isBestApr || isBestVolume || isBestTvl

  return (
    <PoolListAccordionListItemDetail key={pool?.pairAddress} className={`${isBestOfOne ? 'best-of-one' : ''}`}>
      <div className="poollist-header">
        <PoolLogo tokenA={token0.token0Address} tokenB={token1.token1Address} size={32} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          <span style={{ fontFamily: 'Unbounded', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {pairName}
            <TagButton onClick={() => saveLink(pool?.pairAddress)}>
              {copyStatus ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: 'unset' }}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-clipboard-check"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
                  <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
                  <path d="M9 14l2 2l4 -4" />
                </svg>
              ) : (
                <svg
                  style={{ marginRight: 'unset' }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-copy"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" />
                  <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
                </svg>
              )}
            </TagButton>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', maxWidth: '160px' }}>
            <Tag variant={pool.version === 'v3' ? 2 : 1}>
              <div style={{ padding: '0 2px' }}>{pool.version === 'v3' ? 'v3' : 'v2'}</div>
            </Tag>
            <Tag variant={1}>
              <div style={{ padding: '0 2px' }}>{pool.fee ? formattedFeeAmount(pool.fee) : '0.3'}%</div>
            </Tag>
            {poolTags}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex' }} className="poollist-infos">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} className="pool-list-infos-apr">
          <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }} className="pool-display-none-desktop">
            APR
          </span>
          <span
            style={{
              fontWeight: 500,
            }}
            className="responsive-text"
          >
            {pool?.apr && pool?.apr > 50 ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="rgb(236, 159, 72)"
                stroke="rgb(233, 66, 30)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-flame"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M12 10.941c2.333 -3.308 .167 -7.823 -1 -8.941c0 3.395 -2.235 5.299 -3.667 6.706c-1.43 1.408 -2.333 3.621 -2.333 5.588c0 3.704 3.134 6.706 7 6.706s7 -3.002 7 -6.706c0 -1.712 -1.232 -4.403 -2.333 -5.588c-2.084 3.353 -3.257 3.353 -4.667 2.235" />
              </svg>
            ) : (
              <></>
            )}
            {pool.apr ? `${roundToDecimalPlace(`${pool.apr}`, '2')}%` : 'N/A'}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} className="pool-list-infos-fee">
          <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }} className="pool-display-none-desktop">
            Daily Fees
          </span>
          <span style={{ fontWeight: 500 }} className="responsive-text">
            {pool.dailyFees ? `$${roundToDecimalPlace(`${pool.dailyFees}`, '2')}` : 'N/A'}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} className="pool-list-infos-tvl">
          <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }} className="pool-display-none-desktop">
            TVL
          </span>
          <span style={{ fontWeight: 500 }} className="responsive-text">
            {pool.tvl ? `$${roundToDecimalPlace(`${pool.tvl}`, '2')}` : 'N/A'}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} className="pool-list-infos-volume">
          <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }} className="pool-display-none-desktop">
            1D Volume
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '2px',
            }}
          >
            {volumeIncreasingIcon && volumeIncreasingIcon}{' '}
            <span style={{ fontWeight: 500 }} className="responsive-text">
              {pool.h24 ? `$${roundToDecimalPlace(`${pool.h24}`, '2')}` : 'N/A'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} className="pool-list-infos-reserve">
          <span style={{ color: '#c3c5cb', fontWeight: 400, fontSize: '14px' }} className="pool-display-none-desktop">
            Reserve
          </span>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TokenLogo token={token0.token0Address} />
              <span style={{ fontSize: '16px', fontWeight: 500 }} className="responsive-text">
                {roundToDecimalPlace(`${formatUnits(pool.reserves.reserve0, token0.token0Decimal)}`, '2')}{' '}
              </span>
            </div>
            <span>/</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TokenLogo token={token1.token1Address} />
              <span style={{ fontSize: '16px', fontWeight: 500 }} className="responsive-text">
                {roundToDecimalPlace(`${formatUnits(pool.reserves.reserve1, token1.token1Decimal)}`, '2')}
              </span>
            </div>
          </div>
        </div>
        <div className="pool-list-infos-btn pool-display-none-mobile">
          <TagButton
            onClick={() => {
              history.push(addLPRoute)
            }}
            style={{ fontSize: '14px', padding: '10px', height: 'unset' }}
          >
            Add Liq.
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right"
              style={{ marginLeft: '4px', marginRight: 'unset' }}
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M9 6l6 6l-6 6" />
            </svg>
          </TagButton>
        </div>
      </div>

      <div
        className="pool-list-infos-btn pool-display-block-mobile pool-display-none-desktop"
        style={{ width: '100%' }}
      >
        <TagButton
          onClick={() => {
            history.push(addLPRoute)
          }}
          style={{ fontSize: '14px', padding: '10px', height: 'unset', width: '100%' }}
        >
          Add Liq.
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right"
            style={{ marginLeft: '4px', marginRight: 'unset' }}
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M9 6l6 6l-6 6" />
          </svg>
        </TagButton>
      </div>
    </PoolListAccordionListItemDetail>
  )
}

export default PoolListCard
